import { tracked } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import BaseRepositoryService from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { CUSTOM_SUBDOMAIN_OPTIONS } from 'uplisting-frontend/models/direct-booking-configuration';
import { DbePlan } from 'uplisting-frontend/models/duda-configuration';
import { AnalyticsEvents } from 'uplisting-frontend/services/analytics';
import { DudaConfigurationValidation } from 'uplisting-frontend/validations/duda-configuration';

const DBE_PLAN_ANALYTICS_EVENT: Record<DbePlan, AnalyticsEvents> = {
  [DbePlan.plus]: AnalyticsEvents.dbePlusEnabled,
  [DbePlan.basic]: AnalyticsEvents.dbePlusDisabled,
};

export default class DudaConfigurationRepositoryService extends BaseRepositoryService<
  ModelRegistry['duda-configuration']
> {
  @service analytics!: Services['analytics'];

  recordName = 'duda-configuration';
  implementMethods = ['findRecord', 'createRecord', 'buildChangeset'];

  validation = DudaConfigurationValidation;

  @tracked currentDudaConfiguration?: ModelRegistry['duda-configuration'];

  public async getDefaultRecord(): Promise<
    ModelRegistry['duda-configuration']
  > {
    try {
      const record = await this.findRecord('me');

      this.currentDudaConfiguration = record;

      return record;
    } catch {
      return this.createDefaultRecord();
    }
  }

  public createDefaultRecord(): ModelRegistry['duda-configuration'] {
    return this.createRecord({
      subdomain: '',
      customDomain: '',
      customSubdomainOption: CUSTOM_SUBDOMAIN_OPTIONS[0],
    });
  }

  public async setDbePlan(plan: DbePlan): Promise<boolean> {
    const record = this.currentDudaConfiguration;

    if (!record || record.dbePlan === plan) {
      return false;
    }

    record.dbePlan = plan;

    try {
      await record.save({ adapterOptions: { action: 'update_plan' } });
    } catch (error) {
      record.rollbackAttributes();
      throw error;
    }

    try {
      this.analytics.trackEvent(DBE_PLAN_ANALYTICS_EVENT[plan]);
    } catch {
      // A blocked/unavailable analytics SDK must not turn a successful plan
      // change into the caller's error path.
    }

    return true;
  }

  public async switchTemplate(templateAlias: string): Promise<boolean> {
    const record = this.currentDudaConfiguration;

    if (!record || record.templateAlias === templateAlias) {
      return false;
    }

    record.templateAlias = templateAlias;

    try {
      await record.save({ adapterOptions: { action: 'switch_template' } });
    } catch (error) {
      record.rollbackAttributes();
      throw error;
    }

    return true;
  }
}

declare module '@ember/service' {
  interface Registry {
    'repositories/duda-configuration': DudaConfigurationRepositoryService;
  }
}
